import { Box } from '@terra/ui-styled-system';
import {
  useMemo,
  useRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';
import { ChatMessageHeader } from './message/ChatMessage';
import type {
  MentionEntity,
  TerraUIMessagePart,
} from '@terra/shared-dto-chat-types';
import type { AgentInfo } from '../types';
import { MentionContextPill } from '../context-pill/MentionContextPill';
import { MessageSkeletonCard } from './MessageSkeletonCard';
import { ThinkingBlock } from './ThinkingBlock';
import { getMessageContentPart } from '../utils/getVercelMessageContentPart';
import type {
  ChatObjectNavigationTarget,
  OnOpenChatObject,
} from './hitl-types';
import { dataQueryObjectLinksFor } from './tools/data-query/data-query-list-rows.utils';
import type { MarkdownPreviewCustomProps } from '../../markdown-preview/MarkdownPreview';

const MENTION_ENTITY_TYPE: Partial<
  Record<ChatObjectNavigationTarget['type'], MentionEntity['entityType']>
> = {
  signal: 'Signal',
  finding: 'Finding',
  endpoint: 'EndPoint',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function objectLinkIndex(node: unknown): number | undefined {
  if (!isRecord(node) || !isRecord(node.properties)) return;

  const raw =
    node.properties.dataObjectLink ?? node.properties['data-object-link'];

  if (typeof raw !== 'string' && typeof raw !== 'number') return;

  const index = Number(raw);

  return Number.isInteger(index) && index >= 0 ? index : undefined;
}

export function AssistantMessageCard({
  message,
  agent,
  noHeader,
  isFinalOutcome,
  isStreaming,
  expansionKey,
  toolResultsById,
  onOpenObject,
}: {
  message: TerraUIMessagePart;
  agent: AgentInfo | undefined;
  noHeader?: boolean;
  isFinalOutcome?: boolean;
  isStreaming?: boolean;
  isNestedInSubAgent?: boolean;
  expansionKey?: string;
  toolResultsById?: Parameters<typeof dataQueryObjectLinksFor>[0];
  onOpenObject?: OnOpenChatObject;
}) {
  const objectLinks = toolResultsById
    ? dataQueryObjectLinksFor(toolResultsById)
    : undefined;
  const objectLinksRef = useRef(objectLinks);
  const onOpenObjectRef = useRef(onOpenObject);

  objectLinksRef.current = objectLinks;
  onOpenObjectRef.current = onOpenObject;
  const markdownComponents =
    useMemo((): MarkdownPreviewCustomProps['components'] => {
      function ObjectLinkSpan({
        node,
        children,
        ...spanProps
      }: {
        node?: unknown;
        children?: ReactNode;
      } & ComponentPropsWithoutRef<'span'>) {
        const index = objectLinkIndex(node);
        const link =
          index === undefined ? undefined : objectLinksRef.current?.[index];
        const entityType = link
          ? MENTION_ENTITY_TYPE[link.target.type]
          : undefined;
        const open = onOpenObjectRef.current;

        if (!link || !entityType || !open) {
          return <span {...spanProps}>{children}</span>;
        }

        return (
          <MentionContextPill
            entityType={entityType}
            entityId={link.target.id}
            label={link.target.label ?? link.phrase}
            onClick={() => onOpenObjectRef.current?.(link.target)}
          />
        );
      }

      return { span: ObjectLinkSpan };
    }, []);
  const header = useMemo(() => {
    if (noHeader) return null;

    return (
      <ChatMessageHeader
        name={agent?.name || 'AI Assistant'}
        role={agent?.role}
        avatarSrc={
          agent?.avatar ||
          (agent?.id ? `/assets/agents/${agent?.id}.webp` : undefined)
        }
      />
    );
  }, [agent, noHeader]);

  const isReasoning = message?.type === 'reasoning';

  const content = useMemo(() => {
    if (!message || isReasoning) return '';

    return getMessageContentPart(message, 'text') ?? '';
  }, [message, isReasoning]);

  const isToolCallPlaceholder = content === '[Tool call]';

  if (!message || !message.type || isToolCallPlaceholder) {
    return null;
  }

  if (isReasoning) {
    return (
      <Box width="100%">
        {header}
        <ThinkingBlock message={message} expansionKey={expansionKey} />
      </Box>
    );
  } else {
    return (
      <Box width="100%">
        <MessageSkeletonCard
          type="assistant"
          header={header}
          markdownSource={content}
          isFinalAssistantOutcome={isFinalOutcome}
          isStreaming={
            isStreaming ??
            (message.type === 'text' && message.state === 'streaming')
          }
          objectLinks={onOpenObject ? objectLinks : undefined}
          components={onOpenObject ? markdownComponents : undefined}
        />
      </Box>
    );
  }
}
